/**
 * Kai Query — shared model
 *
 * Imported by BOTH the Cloudflare Worker (functions/api/ask-kai.js) and the
 * browser (src/utils/kaiQuery.js). It must stay free of DOM and of Worker
 * globals. Keeping the allowlists and the validator here is the whole point:
 * the client and the server cannot disagree about what a valid query is.
 *
 * A KaiQuery is filterState with arrays instead of Sets, plus routing intent.
 * Every dimension here corresponds to something filterState can actually express —
 * anything Nautilus cannot express is reported in `unsupported`, never approximated.
 *
 * @module kai-query-shared
 */

// ---------------------------------------------------------------------------
// Allowlists — the only values that may ever reach filterState
// ---------------------------------------------------------------------------

export const KAI_ENTITIES = ['tasks', 'projects'];
export const KAI_SCOPES = ['active', 'archived'];
export const KAI_VIEWS = ['', 'kanban', 'list', 'calendar'];
export const KAI_MODES = ['replace', 'add', 'remove', 'clear'];

// Mirrors VALID_STATUSES / VALID_PRIORITIES in src/config/constants.js.
// Duplicated rather than imported because the Worker cannot reach into src/.
// tests/unit/kaiVocabulary.test.js asserts the two stay in sync.
export const KAI_STATUSES = ['backlog', 'todo', 'progress', 'review', 'done'];
export const KAI_PRIORITIES = ['low', 'medium', 'high'];

// The 14 presets implemented by matchesDatePreset() in src/utils/filterPredicates.js.
export const KAI_DATE_PRESETS = [
    'no-date', 'has-date', 'overdue', 'end-today', 'end-tomorrow', 'end-7days', 'end-week', 'end-month',
    'no-start-date', 'has-start-date', 'already-started', 'start-today', 'start-tomorrow', 'start-7days',
    'start-week', 'start-month'
];

export const KAI_LINK_TYPES = ['blocks', 'is_blocked_by', 'relates_to', 'has_web_links', 'no_links'];
export const KAI_DATE_FIELDS = ['startDate', 'endDate'];
export const KAI_UPDATED_FILTERS = ['all', '5m', '30m', '24h', 'week', 'month'];

/**
 * Recency ordering for "the 5 most recently modified/created tasks".
 *
 * Nautilus's list view has no persistent sort control, so this is NOT a general
 * sort dimension — it only names a recency ranking that pairs with `limit` to
 * answer "the last N …". '' = no ordering. Newest-first only; there is no
 * arbitrary column sort (that stays unsupported via SORT_PATTERN).
 */
export const KAI_SORTS = ['', 'recent-updated', 'recent-created'];

// Project status is DERIVED by getProjectStatus() in app.js, not stored.
// 'archived' is deliberately absent: it is a route (#projects/archive), not a filter.
export const KAI_PROJECT_STATUSES = ['backlog', 'planning', 'active', 'completed'];
export const KAI_PROJECT_TASK_FILTERS = ['', 'has-tasks', 'no-tasks'];

/** Sentinel used by matchesProject()/matchesTags() for "no project" / "no tags". */
export const KAI_NONE = 'none';

/** Dimensions that only mean anything for entity==='tasks'. */
const TASK_ONLY_DIMENSIONS = [
    'statuses', 'statusExcludeMode', 'priorities', 'priorityExcludeMode',
    'projects', 'projectExcludeMode', 'datePresets', 'dateFrom', 'dateTo',
    'dateField', 'linkTypes', 'createdFrom', 'createdTo', 'updatedFrom', 'updatedTo',
    'completedFrom', 'completedTo', 'duplicateTitles'
];

/** Dimensions that only mean anything for entity==='projects'. */
const PROJECT_ONLY_DIMENSIONS = [
    'projectStatuses', 'projectStatusExcludeMode', 'taskFilter', 'completionMin', 'completionMax'
];

// ---------------------------------------------------------------------------
// Construction
// ---------------------------------------------------------------------------

export function createEmptyFilters() {
    return {
        // tasks — mirrors app.js filterState
        search: '',
        statuses: [],
        statusExcludeMode: false,
        priorities: [],
        priorityExcludeMode: false,
        projects: [],
        projectExcludeMode: false,
        tags: [],
        tagExcludeMode: false,
        tagAndMode: false,
        datePresets: [],
        dateFrom: '',
        dateTo: '',
        dateField: 'endDate',
        // Activity windows (inclusive ISO date bounds), one pair per timestamp. These
        // are independent of the due-date range above, so "created last week and due
        // today" applies both.
        createdFrom: '', createdTo: '',
        updatedFrom: '', updatedTo: '',
        completedFrom: '', completedTo: '',
        linkTypes: [],
        updatedFilter: 'all',
        limit: null, // Numerical result limit (e.g., "5 most recently updated")
        sort: '',    // Recency ordering that pairs with limit — see KAI_SORTS
        // True = only tasks whose title is shared by at least one other task
        // ("show me tasks with the same title"). Computed in filterTasks().
        duplicateTitles: false,
        // projects — mirrors app.js projectFilterState
        projectStatuses: [],
        projectStatusExcludeMode: false,
        taskFilter: '',
        // Completion percentage bounds, inclusive. null = unbounded.
        completionMin: null,
        completionMax: null
    };
}

export function createEmptyKaiQuery(entity = 'tasks') {
    return {
        entity: KAI_ENTITIES.includes(entity) ? entity : 'tasks',
        scope: 'active',
        view: '',
        mode: 'replace',
        filters: createEmptyFilters(),
        unsupported: [],
        unresolved: [],
        explanation: ''
    };
}

/** True when the query would apply no filter at all (an empty result view). */
export function isEmptyKaiQuery(query) {
    const f = query?.filters;
    if (!f) return true;
    const arrays = ['statuses', 'priorities', 'projects', 'tags', 'datePresets', 'linkTypes', 'projectStatuses'];
    if (arrays.some(key => Array.isArray(f[key]) && f[key].length > 0)) return false;
    if (f.search) return false;
    if (f.dateFrom || f.dateTo) return false;
    if (f.createdFrom || f.createdTo || f.updatedFrom || f.updatedTo || f.completedFrom || f.completedTo) return false;
    if (f.taskFilter) return false;
    if (f.completionMin != null || f.completionMax != null) return false;
    if (f.updatedFilter && f.updatedFilter !== 'all') return false;
    if (f.duplicateTitles) return false;
    // "the 5 most recently modified" ranks the whole task set — a real answer, not
    // an empty view — so a recency sort makes the query viewable on its own.
    if (f.sort) return false;
    // Scope is a route, not a filter, but "show archived tasks" is a real request.
    if (query.scope === 'archived') return false;
    return true;
}

/** Count the filter dimensions in play — used for review copy and Apply gating. */
export function countKaiQueryFilters(query) {
    const f = query?.filters;
    if (!f) return 0;
    let n = 0;
    for (const key of ['statuses', 'priorities', 'projects', 'tags', 'datePresets', 'linkTypes', 'projectStatuses']) {
        if (Array.isArray(f[key]) && f[key].length > 0) n++;
    }
    if (f.search) n++;
    if (f.dateFrom || f.dateTo) n++;
    if (f.createdFrom || f.createdTo) n++;
    if (f.updatedFrom || f.updatedTo) n++;
    if (f.completedFrom || f.completedTo) n++;
    if (f.taskFilter) n++;
    if (f.completionMin != null || f.completionMax != null) n++;
    if (f.updatedFilter && f.updatedFilter !== 'all') n++;
    if (f.duplicateTitles) n++;
    if (f.sort) n++;
    return n;
}

// ---------------------------------------------------------------------------
// Normalization — coerce untrusted input (LLM output, URL, storage) into shape
// ---------------------------------------------------------------------------

function toStringArray(value) {
    if (!Array.isArray(value)) return [];
    const seen = new Set();
    const out = [];
    for (const item of value) {
        if (typeof item !== 'string' && typeof item !== 'number') continue;
        const s = String(item).trim();
        if (!s || seen.has(s)) continue;
        seen.add(s);
        out.push(s);
    }
    return out;
}

function pickEnum(value, allowed, fallback) {
    return allowed.includes(value) ? value : fallback;
}

function toBool(value) {
    return value === true;
}

/** A 0-100 percentage, or null. Out-of-range values are discarded, never clamped. */
function toPercent(value) {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    if (!Number.isFinite(number)) return null;
    const rounded = Math.round(number);
    if (rounded < 0 || rounded > 100) return null;
    return rounded;
}

/** ISO date (YYYY-MM-DD) or '' — anything else is discarded. */
function toISODate(value) {
    const s = String(value || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '';
    const d = new Date(`${s}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return '';
    // Reject impossible dates that Date silently rolls over (2026-02-31 → Mar 3).
    if (d.toISOString().slice(0, 10) !== s) return '';
    return s;
}

function toNoteArray(value, kindAllowlist) {
    if (!Array.isArray(value)) return [];
    return value
        .filter(item => item && typeof item === 'object')
        .map(item => {
            const note = { term: String(item.term || '').trim() };
            if (kindAllowlist) {
                note.kind = kindAllowlist.includes(item.kind) ? item.kind : 'unknown';
                note.candidates = toStringArray(item.candidates);
                // Which KINDS of thing this name could be ('tag' | 'project'). Lets the
                // UI offer "Tag: X" / "Project: X" instead of a bare, ambiguous name.
                const kinds = toStringArray(item.kinds).filter(kind => kind === 'tag' || kind === 'project');
                if (kinds.length) note.kinds = kinds;
            } else {
                note.reason = String(item.reason || '').trim();
            }
            return note;
        })
        .filter(note => note.term);
}

/**
 * Coerce anything into a structurally valid KaiQuery.
 * Does NOT check values against live data — that's validateKaiQueryAgainstVocabulary.
 */
export function normalizeKaiQuery(raw) {
    const src = raw && typeof raw === 'object' ? raw : {};
    const rawFilters = src.filters && typeof src.filters === 'object' ? src.filters : {};
    const base = createEmptyFilters();

    const filters = {
        search: String(rawFilters.search || '').trim().slice(0, 200),
        statuses: toStringArray(rawFilters.statuses),
        statusExcludeMode: toBool(rawFilters.statusExcludeMode),
        priorities: toStringArray(rawFilters.priorities),
        priorityExcludeMode: toBool(rawFilters.priorityExcludeMode),
        projects: toStringArray(rawFilters.projects),
        projectExcludeMode: toBool(rawFilters.projectExcludeMode),
        tags: toStringArray(rawFilters.tags),
        tagExcludeMode: toBool(rawFilters.tagExcludeMode),
        tagAndMode: toBool(rawFilters.tagAndMode),
        datePresets: toStringArray(rawFilters.datePresets),
        dateFrom: toISODate(rawFilters.dateFrom),
        dateTo: toISODate(rawFilters.dateTo),
        dateField: pickEnum(rawFilters.dateField, KAI_DATE_FIELDS, base.dateField),
        createdFrom: toISODate(rawFilters.createdFrom),
        createdTo: toISODate(rawFilters.createdTo),
        updatedFrom: toISODate(rawFilters.updatedFrom),
        updatedTo: toISODate(rawFilters.updatedTo),
        completedFrom: toISODate(rawFilters.completedFrom),
        completedTo: toISODate(rawFilters.completedTo),
        linkTypes: toStringArray(rawFilters.linkTypes),
        updatedFilter: pickEnum(rawFilters.updatedFilter, KAI_UPDATED_FILTERS, base.updatedFilter),
        limit: typeof rawFilters.limit === 'number' && rawFilters.limit > 0 ? rawFilters.limit : null,
        sort: pickEnum(rawFilters.sort, KAI_SORTS, base.sort),
        duplicateTitles: toBool(rawFilters.duplicateTitles),
        projectStatuses: toStringArray(rawFilters.projectStatuses),
        projectStatusExcludeMode: toBool(rawFilters.projectStatusExcludeMode),
        taskFilter: pickEnum(rawFilters.taskFilter, KAI_PROJECT_TASK_FILTERS, base.taskFilter),
        completionMin: toPercent(rawFilters.completionMin),
        completionMax: toPercent(rawFilters.completionMax)
    };

    return {
        entity: pickEnum(src.entity, KAI_ENTITIES, 'tasks'),
        scope: pickEnum(src.scope, KAI_SCOPES, 'active'),
        view: pickEnum(src.view, KAI_VIEWS, ''),
        mode: pickEnum(src.mode, KAI_MODES, 'replace'),
        filters,
        unsupported: toNoteArray(src.unsupported, null),
        unresolved: toNoteArray(src.unresolved, ['unknown', 'ambiguous']),
        explanation: String(src.explanation || '').trim().slice(0, 500)
    };
}

// ---------------------------------------------------------------------------
// Validation against live data — the anti-hallucination gate
// ---------------------------------------------------------------------------

function normalizeName(value) {
    return String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Drop every value that does not exist in the user's real data, and say so.
 *
 * This runs on BOTH sides: the server runs it on the model's output before
 * returning, and the client runs it again before touching filterState. LLM
 * output is untrusted input.
 *
 * @param {Object} query - a normalized KaiQuery
 * @param {Object} vocabulary - from buildKaiVocabulary()
 * @returns {{query: Object, unresolved: Array, warnings: Array, blockingIssues: Array}}
 */
export function validateKaiQueryAgainstVocabulary(query, vocabulary = {}) {
    const q = normalizeKaiQuery(query);
    const f = q.filters;
    const unresolved = [...q.unresolved];
    const warnings = [];
    const blockingIssues = [];

    const allowedStatuses = Array.isArray(vocabulary.statuses) && vocabulary.statuses.length
        ? vocabulary.statuses
        : KAI_STATUSES;
    const allowedPriorities = Array.isArray(vocabulary.priorities) && vocabulary.priorities.length
        ? vocabulary.priorities
        : KAI_PRIORITIES;

    const knownTags = new Map();
    for (const tag of (vocabulary.tags || [])) {
        const name = typeof tag === 'string' ? tag : tag?.name;
        if (name) knownTags.set(normalizeName(name), String(name));
    }
    const knownProjectIds = new Set();
    const knownProjectNames = new Map();
    for (const project of (vocabulary.projects || [])) {
        if (!project) continue;
        knownProjectIds.add(String(project.id));
        if (project.name) knownProjectNames.set(normalizeName(project.name), String(project.id));
    }

    // Enum dimensions — reject anything outside the allowlist outright.
    const enumChecks = [
        ['statuses', allowedStatuses],
        ['priorities', allowedPriorities],
        ['datePresets', KAI_DATE_PRESETS],
        ['linkTypes', KAI_LINK_TYPES],
        ['projectStatuses', KAI_PROJECT_STATUSES]
    ];
    for (const [key, allowed] of enumChecks) {
        const kept = [];
        for (const value of f[key]) {
            if (allowed.includes(value)) {
                kept.push(value);
            } else {
                unresolved.push({ term: value, kind: 'unknown', candidates: [] });
                warnings.push({ code: 'UNKNOWN_VALUE', field: key, term: value });
            }
        }
        f[key] = kept;
    }

    // Projects — must be a real id (or the "none" sentinel). Accept a name and
    // resolve it, since a model will reach for the name it was shown.
    const keptProjects = [];
    for (const value of f.projects) {
        if (value === KAI_NONE) { keptProjects.push(KAI_NONE); continue; }
        if (knownProjectIds.has(value)) { keptProjects.push(value); continue; }
        const byName = knownProjectNames.get(normalizeName(value));
        if (byName) { keptProjects.push(byName); continue; }
        unresolved.push({ term: value, kind: 'unknown', candidates: nearestNames(value, [...knownProjectNames.keys()]) });
        warnings.push({ code: 'UNKNOWN_PROJECT', field: 'projects', term: value });
    }
    f.projects = [...new Set(keptProjects)];

    // Tags — must be a real tag name (or "none"). Case-insensitive, accent-insensitive.
    const keptTags = [];
    for (const value of f.tags) {
        if (value === KAI_NONE) { keptTags.push(KAI_NONE); continue; }
        const canonical = knownTags.get(normalizeName(value));
        if (canonical) { keptTags.push(canonical); continue; }
        unresolved.push({ term: value, kind: 'unknown', candidates: nearestNames(value, [...knownTags.values()]) });
        warnings.push({ code: 'UNKNOWN_TAG', field: 'tags', term: value });
    }
    f.tags = [...new Set(keptTags)];

    // A filter that selects EVERY possible value of a dimension filters nothing.
    // An LLM asked to "filter tasks" will sometimes echo the whole allowlist back —
    // every status, every priority, every tag — which is not a filter, it is noise,
    // and combined with any other filter it silently matches nothing. Drop it.
    // Selecting every real value is a no-op even without the "none" sentinel, so the
    // threshold is the count of real values — an LLM echoing the whole project list
    // back must be dropped whether or not it also tacked on "none".
    const noOpChecks = [
        ['statuses', allowedStatuses.length, 'statusExcludeMode'],
        ['priorities', allowedPriorities.length, 'priorityExcludeMode'],
        ['projects', knownProjectIds.size, 'projectExcludeMode'],
        ['tags', knownTags.size, 'tagExcludeMode'],
        ['projectStatuses', KAI_PROJECT_STATUSES.length, 'projectStatusExcludeMode']
    ];
    for (const [key, total, excludeKey] of noOpChecks) {
        // In exclude mode the same set means the opposite — "everything except
        // everything" — which is a contradiction, not a no-op. Leave it to fail
        // loudly rather than silently reinterpreting it.
        if (f[excludeKey]) continue;
        // When every real value is present the whole selection is an echo-everything
        // no-op; drop it entirely, including a "none" tacked onto the same garbage.
        const realCount = f[key].filter(value => value !== KAI_NONE).length;
        if (total > 1 && realCount >= total) {
            warnings.push({ code: 'NO_OP_FILTER', field: key });
            f[key] = [];
        }
    }

    // Something declared unsupported must not reappear as a filter value.
    //
    // A model that cannot express "more than 75% of tasks done" will sometimes
    // report it as unsupported AND drop the same phrase into the free-text search,
    // which then matches nothing. Saying "I can't do this" and quietly doing
    // something else is the worst of both.
    const searchNormalized = normalizeName(f.search);
    if (searchNormalized && q.unsupported.some(note => {
        const term = normalizeName(note.term);
        return term && (term === searchNormalized || searchNormalized.includes(term));
    })) {
        warnings.push({ code: 'UNSUPPORTED_SEARCH_DROPPED', term: f.search });
        f.search = '';
    }

    // Contradictory range — do not guess an intent, refuse the filter.
    for (const [from, to] of [['dateFrom', 'dateTo'], ['createdFrom', 'createdTo'], ['updatedFrom', 'updatedTo'], ['completedFrom', 'completedTo']]) {
        if (f[from] && f[to] && f[from] > f[to]) {
            blockingIssues.push({ code: 'CONTRADICTORY_DATE_RANGE', from: f[from], to: f[to] });
            f[from] = '';
            f[to] = '';
        }
    }

    // "More than 90% but less than 10% done" is not a filter, it is a contradiction.
    if (f.completionMin != null && f.completionMax != null && f.completionMin > f.completionMax) {
        blockingIssues.push({ code: 'CONTRADICTORY_COMPLETION', from: f.completionMin, to: f.completionMax });
        f.completionMin = null;
        f.completionMax = null;
    }

    // An explicit range already requires the date to exist, so a "has-date" /
    // "has-start-date" existence preset is redundant beside it. "tasks with an end
    // date in March" means the March range, not "any task that has an end date" —
    // drop the preset, keep the range.
    if (f.dateFrom || f.dateTo) {
        f.datePresets = f.datePresets.filter(preset => preset !== 'has-date' && preset !== 'has-start-date');
    }

    // filterTasks() ignores the range whenever ANY preset is still present. Mirror
    // that here so the review never promises a filter that won't be applied.
    if (f.datePresets.length > 0 && (f.dateFrom || f.dateTo)) {
        warnings.push({ code: 'DATE_RANGE_IGNORED' });
        f.dateFrom = '';
        f.dateTo = '';
    }

    // Cross-entity dimensions are silently meaningless — zero them and say so.
    if (q.entity === 'projects') {
        const dropped = TASK_ONLY_DIMENSIONS.filter(key => hasValue(f[key]));
        if (dropped.length) {
            warnings.push({ code: 'TASK_DIMENSIONS_DROPPED', fields: dropped });
            resetKeys(f, TASK_ONLY_DIMENSIONS);
        }
    } else {
        const dropped = PROJECT_ONLY_DIMENSIONS.filter(key => hasValue(f[key]));
        if (dropped.length) {
            warnings.push({ code: 'PROJECT_DIMENSIONS_DROPPED', fields: dropped });
            resetKeys(f, PROJECT_ONLY_DIMENSIONS);
        }
    }

    // A "none" tag filter is meaningless when every task is tagged.
    if (f.tags.includes(KAI_NONE) && vocabulary.hasUntaggedTasks === false) {
        warnings.push({ code: 'NO_UNTAGGED_TASKS' });
    }

    q.unresolved = dedupeNotes(unresolved);
    return { query: q, unresolved: q.unresolved, warnings, blockingIssues };
}

function hasValue(value) {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'boolean') return value === true;
    if (typeof value === 'string') return value !== '' && value !== 'endDate' && value !== 'all';
    return false;
}

function resetKeys(filters, keys) {
    const base = createEmptyFilters();
    for (const key of keys) filters[key] = base[key];
}

function dedupeNotes(notes) {
    const seen = new Set();
    return notes.filter(note => {
        const key = `${note.kind || ''}:${normalizeName(note.term)}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

/**
 * The "none" sentinel (no project / untagged) is a specific, deliberate filter. A
 * model will sometimes add it on its own — reading "show me Franky's tasks" as
 * "…that also have no project" — which quietly empties the result.
 *
 * So it is only ever honoured when the user's own words asked for it. This runs on
 * LLM output (client and server); the local parser already gates it behind an
 * explicit phrase, so it is a no-op there.
 */
// Split by target: "tasks with no tags" asks for the tag-none sentinel but says
// nothing about projects, so a project-none the model tacked on must still be
// stripped. Apostrophes are already normalized away ("don't" → "dont").
const NONE_TAG_INTENT_RE = /\b(untagged|no tags?|without (?:any )?tags?|do ?nt? have (?:any )?tags?|have no tags?|no assignee|unassigned|assigned to (?:nobody|no ?one)|no owner|without an owner|do ?nt? have an owner|nobody|sin (?:etiquetas?|asignar|responsable)|no tienen etiquetas?|nadie)\b/i;
const NONE_PROJECT_INTENT_RE = /\b(no project|without a project|projectless|do ?nt? have a project|have no project|no tienen proyecto|sin proyecto)\b/i;
// "archived" is a storage scope, not a status. The LLM tends to conflate "closed"/
// "cerrado" with it, so only honour an archived scope when the user literally asked
// for archived/archivado(s). Matches archive, archived, archivado/a/os/as.
const ARCHIVED_INTENT_RE = /archiv/i;

export function stripUnaskedNoneSentinels(query, instructionText = '') {
    const q = normalizeKaiQuery(query);
    const text = String(instructionText || '');
    if (!NONE_TAG_INTENT_RE.test(text)) {
        q.filters.tags = q.filters.tags.filter(value => value !== KAI_NONE);
    }
    if (!NONE_PROJECT_INTENT_RE.test(text)) {
        q.filters.projects = q.filters.projects.filter(value => value !== KAI_NONE);
    }
    return q;
}

/**
 * Drop an archived scope the user never asked for. The LLM fallback conflates
 * "closed"/"cerrado" (a completion status) with the archived storage scope, which
 * ANDs against the real filter and yields zero rows. Only keep scope:archived when
 * the instruction actually mentions archiving.
 */
export function stripUnaskedArchivedScope(query, instructionText = '') {
    const q = normalizeKaiQuery(query);
    if (q.scope === 'archived' && !ARCHIVED_INTENT_RE.test(String(instructionText || ''))) {
        q.scope = 'active';
    }
    return q;
}

// End-date/deadline presets. Asking "what's overdue / due this week" in a task
// manager means OPEN work — a finished task is not something you still owe.
// Start-date presets are about when work begins, not a deadline, so they are
// left alone (and 'already-started' already guards status !== 'done' itself).
const DUE_PRESETS = ['overdue', 'end-today', 'end-tomorrow', 'end-7days', 'end-week', 'end-month'];
// Only widen back to include finished tasks when the user says so explicitly.
// Covers EN (done/completed/finished/resolved/closed) and ES (hecho/completado/
// terminado/finalizado/cerrado/resuelto). Apostrophes are already normalized away.
const INCLUDE_DONE_INTENT_RE = /\b(done|completed|finished|resolved|closed|hech[oa]s?|completad[oa]s?|terminad[oa]s?|finalizad[oa]s?|cerrad[oa]s?|resuelt[oa]s?)\b/i;

/**
 * Default due/overdue queries to OPEN tasks. Without this, "show overdue tasks"
 * returns everything past its end date INCLUDING tasks already marked done —
 * matchesDatePreset('overdue') checks only endDate < today, and an empty status
 * filter passes every status. Every other overdue site in the app guards
 * status !== 'done'; this makes the Kai layer match that convention.
 *
 * Applied only when the user did not already constrain status and did not
 * explicitly ask to include completed work. Idempotent; a no-op otherwise.
 */
export function excludeDoneForDueQueries(query, instructionText = '') {
    const q = normalizeKaiQuery(query);
    const hasDuePreset = q.filters.datePresets.some(preset => DUE_PRESETS.includes(preset));
    const hasExactDeadline = q.entity === 'tasks' && q.filters.dateField === 'endDate'
        && q.filters.dateFrom && q.filters.dateFrom === q.filters.dateTo;
    const statusUnconstrained = q.filters.statuses.length === 0;
    const asksForDone = INCLUDE_DONE_INTENT_RE.test(String(instructionText || ''));
    if ((hasDuePreset || hasExactDeadline) && statusUnconstrained && !asksForDone) {
        q.filters.statuses = ['done'];
        q.filters.statusExcludeMode = true;
    }
    return q;
}

/** Structural words users put before a project name ("Obj. 3: …", "Proyecto …"). */
export const PROJECT_NAME_PREFIX_WORDS = ['obj', 'objetivo', 'objective', 'proyecto', 'project'];
const PROJECT_NAME_PREFIX = /^\s*(?:obj(?:etivo)?|objective|proj(?:ect)?|proyecto)\b\.?\s*(\d+)?\s*[:.\-–—]?\s*/iu;

/**
 * Names a user may say for one project, full name first: the name without its
 * structural prefix ("Obj. 1: CAMPAÑA 3" → "CAMPAÑA 3") and that prefix spelled
 * with any ES/EN synonym ("objetivo 1 CAMPAÑA 3"). Raw text — callers normalize.
 */
export function projectNameAliases(name) {
    const full = String(name || '').trim();
    if (!full) return [];
    const match = full.match(PROJECT_NAME_PREFIX);
    const number = match?.[1] || '';
    const body = match ? full.slice(match[0].length).trim() : '';
    if (!match || (!body && !number)) return [full];
    const aliases = [full];
    if (body) aliases.push(body);
    for (const word of PROJECT_NAME_PREFIX_WORDS) aliases.push([word, number, body].filter(Boolean).join(' '));
    return [...new Set(aliases)];
}

/** Levenshtein-nearest names, for "did you mean" candidates. Cheap: n is small. */
export function nearestNames(term, names, limit = 3) {
    const needle = normalizeName(term);
    if (!needle) return [];
    return names
        .map(name => ({ name, distance: levenshtein(needle, normalizeName(name)) }))
        .filter(entry => entry.distance <= Math.max(2, Math.floor(needle.length / 3)))
        .sort((a, b) => a.distance - b.distance || a.name.localeCompare(b.name))
        .slice(0, limit)
        .map(entry => entry.name);
}

export function levenshtein(a, b) {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;
    let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i++) {
        const curr = [i];
        for (let j = 1; j <= b.length; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
        }
        prev = curr;
    }
    return prev[b.length];
}

/**
 * The review model shown in the drawer before the user hits Apply.
 *
 * Built here rather than on either side alone, so a locally-parsed query and an
 * LLM-parsed one produce an identical review — and Apply is gated by the same
 * rule in both cases.
 */
export function buildKaiQueryReview(query, validation = {}) {
    const q = normalizeKaiQuery(query);
    const filterCount = countKaiQueryFilters(q);
    const blockingIssues = validation.blockingIssues || [];
    const unresolved = q.unresolved || [];
    const unsupported = q.unsupported || [];

    // An ambiguous name must be settled by the user — picking one arbitrarily is
    // exactly the failure mode this feature is supposed to avoid.
    const hasAmbiguity = unresolved.some(note => note.kind === 'ambiguous');
    const hasNothingToApply = filterCount === 0 && q.scope !== 'archived' && q.mode !== 'clear';

    return {
        type: 'query_filter',
        summary: q.explanation || '',
        entity: q.entity,
        scope: q.scope,
        mode: q.mode,
        filterCount,
        unresolved,
        unsupported,
        blockingIssues,
        warnings: validation.warnings || [],
        canApply: blockingIssues.length === 0 && !hasAmbiguity && !hasNothingToApply,
        previewCount: filterCount,
        overflowCount: 0
    };
}

// ---------------------------------------------------------------------------
// Follow-up merging — "only the urgent ones", "remove Franky", "clear filters"
// ---------------------------------------------------------------------------

const ARRAY_DIMENSIONS = [
    'statuses', 'priorities', 'projects', 'tags', 'datePresets', 'linkTypes', 'projectStatuses'
];

const SCALAR_DEFAULTS = createEmptyFilters();

/**
 * Combine a follow-up query with the one already applied.
 *
 * replace — the follow-up wins outright (a fresh request)
 * add     — union the arrays, keep everything from before ("only the urgent ones")
 * remove  — subtract the follow-up's values from what's applied ("drop Franky")
 * clear   — back to nothing
 *
 * Switching entity forces `replace`: task filters are meaningless on projects.
 */
export function mergeKaiQuery(previous, next, mode) {
    const incoming = normalizeKaiQuery(next);
    const requested = KAI_MODES.includes(mode) ? mode : incoming.mode;

    if (requested === 'clear') {
        const cleared = createEmptyKaiQuery(incoming.entity);
        cleared.explanation = incoming.explanation;
        cleared.mode = 'clear';
        return cleared;
    }

    if (!previous) return { ...incoming, mode: 'replace' };

    const prev = normalizeKaiQuery(previous);

    if (requested === 'replace' || prev.entity !== incoming.entity) {
        return { ...incoming, mode: 'replace' };
    }

    const merged = normalizeKaiQuery(prev);
    merged.mode = requested;
    merged.explanation = incoming.explanation;
    merged.unsupported = incoming.unsupported;
    merged.unresolved = incoming.unresolved;
    if (incoming.scope !== 'active') merged.scope = incoming.scope;
    if (incoming.view) merged.view = incoming.view;

    const a = merged.filters;
    const b = incoming.filters;

    if (requested === 'add') {
        for (const key of ARRAY_DIMENSIONS) {
            a[key] = [...new Set([...a[key], ...b[key]])];
        }
        // A scalar only overrides when the follow-up actually set it — otherwise
        // an empty default would silently wipe the filter we're refining.
        for (const key of ['search', 'dateFrom', 'dateTo', 'dateField', 'updatedFilter', 'taskFilter', 'sort', 'limit']) {
            if (b[key] !== SCALAR_DEFAULTS[key]) a[key] = b[key];
        }
        for (const key of ['statusExcludeMode', 'priorityExcludeMode', 'projectExcludeMode', 'tagExcludeMode', 'tagAndMode', 'projectStatusExcludeMode', 'duplicateTitles']) {
            if (b[key] === true) a[key] = true;
        }
    } else if (requested === 'remove') {
        for (const key of ARRAY_DIMENSIONS) {
            if (b[key].length) {
                const drop = new Set(b[key]);
                a[key] = a[key].filter(value => !drop.has(value));
            }
        }
        for (const key of ['search', 'dateFrom', 'dateTo', 'taskFilter', 'sort', 'limit']) {
            if (b[key] !== SCALAR_DEFAULTS[key]) a[key] = SCALAR_DEFAULTS[key];
        }
        if (b.updatedFilter !== SCALAR_DEFAULTS.updatedFilter) a.updatedFilter = SCALAR_DEFAULTS.updatedFilter;
        if (b.duplicateTitles) a.duplicateTitles = false;
    }

    // Presets beat ranges (filterTasks precedence) — keep the merged result honest.
    if (a.datePresets.length && (a.dateFrom || a.dateTo)) {
        a.dateFrom = '';
        a.dateTo = '';
    }

    return merged;
}
